import { useEffect, useMemo, useState } from 'react';
import { Calculator, Check, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, formatEur, inputCls, selectCls } from '../../utils/operations';

const emptyLine = {
  description: '', partner_name: '', quantity: '1', unit_net_eur: '',
  show_to_client: false, client_label: '', client_price_eur: '', internal_note: '',
};

const toFormLine = (line) => ({
  description: line.description || '',
  partner_name: line.partner_name || '',
  quantity: String(line.quantity ?? 1),
  unit_net_eur: String(line.unit_net_eur ?? ''),
  show_to_client: Boolean(line.show_to_client),
  client_label: line.client_label || '',
  client_price_eur: line.client_price_eur === null || line.client_price_eur === undefined ? '' : String(line.client_price_eur),
  internal_note: line.internal_note || '',
});

export const AdminProposalCalculator = ({ request, partners, onApply }) => {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedDraft, setSavedDraft] = useState(null);
  const [marginPct, setMarginPct] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [lines, setLines] = useState([emptyLine]);

  useEffect(() => {
    if (!open || loaded) return;
    const load = async () => {
      setLoading(true);
      try {
        const [settingsResult, draftResult] = await Promise.allSettled([
          apiFetch('/settings'),
          apiFetch(`/admin/requests/${request.id}/costing`),
        ]);
        if (settingsResult.status === 'fulfilled') setMarginPct(String(settingsResult.value.default_margin_pct ?? 20));
        if (draftResult.status === 'fulfilled') {
          const draft = draftResult.value;
          setSavedDraft(draft);
          setMarginPct(String(draft.margin_pct ?? settingsResult.value?.default_margin_pct ?? 20));
          setInternalNotes(draft.internal_notes || '');
          setLines((draft.lines || []).map(toFormLine));
        }
        setDirty(false);
      } finally {
        setLoaded(true);
        setLoading(false);
      }
    };
    load();
  }, [loaded, open, request.id]);

  const totals = useMemo(() => {
    const net = lines.reduce((sum, line) => sum + ((Number(line.quantity) || 0) * (Number(line.unit_net_eur) || 0)), 0);
    const margin = marginPct === '' ? 0 : Number(marginPct) || 0;
    const clientTotal = net * (1 + margin / 100);
    const selectedClientTotal = lines.reduce((sum, line) => sum + (line.show_to_client ? (Number(line.client_price_eur) || 0) : 0), 0);
    return {
      net: Math.round(net * 100) / 100,
      marginAmount: Math.round((clientTotal - net) * 100) / 100,
      clientTotal: Math.round(clientTotal * 100) / 100,
      selectedClientTotal: Math.round(selectedClientTotal * 100) / 100,
    };
  }, [lines, marginPct]);

  const setLine = (index, key, value) => {
    setLines((current) => current.map((line, i) => (i === index ? { ...line, [key]: value } : line)));
    setDirty(true);
  };

  const removeLine = (index) => {
    setLines((current) => (current.length === 1 ? [emptyLine] : current.filter((_, i) => i !== index)));
    setDirty(true);
  };

  const applyToProposal = async () => {
    const payloadLines = lines
      .filter((line) => line.description.trim() || line.unit_net_eur !== '')
      .map((line) => ({
        description: line.description.trim(),
        partner_name: line.partner_name,
        quantity: Number(line.quantity),
        unit_net_eur: Number(line.unit_net_eur),
        show_to_client: Boolean(line.show_to_client),
        client_label: line.show_to_client ? line.client_label.trim() : '',
        client_price_eur: line.show_to_client && line.client_price_eur !== '' ? Number(line.client_price_eur) : null,
        internal_note: line.internal_note,
      }));
    if (!payloadLines.length) {
      toast.error('Adicione pelo menos uma linha de custo antes de aplicar');
      return;
    }
    setSaving(true);
    try {
      const draft = await apiFetch(`/admin/requests/${request.id}/costing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          margin_pct: marginPct === '' ? null : Number(marginPct),
          lines: payloadLines,
          internal_notes: internalNotes,
        }),
      });
      setSavedDraft(draft);
      setDirty(false);
      onApply(draft);
      toast.success('Costing guardado e aplicado ao editor da proposta', {
        description: 'A proposta não foi enviada, publicada ou aprovada.',
      });
    } catch (err) {
      toast.error('Costing não aplicado', { description: String(err.message) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-4 rounded-xl border border-eco-ink/10 bg-white" data-testid={`costing-calculator-${request.id}`}>
      <button type="button" data-testid={`costing-toggle-${request.id}`} onClick={() => setOpen((value) => !value)} className="flex min-h-12 w-full items-center justify-between rounded-xl px-4 text-left text-sm font-semibold text-eco-ink transition-colors hover:bg-eco-cream/60">
        <span className="inline-flex items-center gap-2"><Calculator size={16} className="text-eco-gold" /> Calculadora comercial privada</span>
        <span className="text-xs text-eco-ink/45">{open ? 'Fechar' : 'Abrir'}</span>
      </button>

      {open && (
        <div className="border-t border-eco-ink/10 p-4">
          <p className="rounded-lg bg-eco-cream px-3 py-2 text-xs leading-relaxed text-eco-ink/60" data-testid={`costing-privacy-note-${request.id}`}>
            Ferramenta interna. Edições são temporárias até clicar em <strong>Apply to proposal</strong>. Custos NET, margens e notas nunca aparecem no site, API pública, brochura ou proposta do cliente.
          </p>
          {loading ? <p className="mt-4 inline-flex items-center gap-2 text-sm text-eco-ink/55"><Loader2 size={15} className="animate-spin" /> A carregar rascunho privado…</p> : (
            <>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <label className="text-xs font-semibold uppercase tracking-wide text-eco-ink/50">Margem Eco %
                  <input data-testid={`costing-margin-${request.id}`} type="number" min="0" max="80" step="0.1" value={marginPct} onChange={(e) => { setMarginPct(e.target.value); setDirty(true); }} className={`${inputCls} mt-1 w-32`} />
                </label>
                <p className="text-xs text-eco-ink/50">Base de cálculo: EUR. A moeda do cliente continua a ser escolhida no editor da proposta.</p>
              </div>

              <div className="mt-4 space-y-3" data-testid={`costing-lines-${request.id}`}>
                {lines.map((line, index) => (
                  <div key={index} className="rounded-xl border border-eco-ink/10 bg-eco-cream/40 p-3" data-testid={`costing-line-${request.id}-${index}`}>
                    <div className="grid gap-3 lg:grid-cols-[1.4fr_1fr_0.45fr_0.65fr_auto]">
                      <input data-testid={`costing-description-${request.id}-${index}`} value={line.description} onChange={(e) => setLine(index, 'description', e.target.value)} placeholder="Serviço / custo interno" className={inputCls} />
                      <select data-testid={`costing-partner-${request.id}-${index}`} value={line.partner_name} onChange={(e) => setLine(index, 'partner_name', e.target.value)} className={selectCls}>
                        <option value="">Sem parceiro associado</option>
                        {partners.map((partner) => <option key={partner.id} value={partner.name}>{partner.name}</option>)}
                      </select>
                      <input data-testid={`costing-quantity-${request.id}-${index}`} type="number" min="0.01" step="0.01" value={line.quantity} onChange={(e) => setLine(index, 'quantity', e.target.value)} placeholder="Qtd." className={inputCls} />
                      <input data-testid={`costing-net-${request.id}-${index}`} type="number" min="0" step="0.01" value={line.unit_net_eur} onChange={(e) => setLine(index, 'unit_net_eur', e.target.value)} placeholder="NET unitário €" className={inputCls} />
                      <button type="button" data-testid={`costing-remove-${request.id}-${index}`} onClick={() => removeLine(index)} className="inline-flex min-h-10 items-center justify-center rounded-lg border border-red-200 px-3 text-red-600 transition-colors hover:bg-red-50" aria-label="Remover linha"><Trash2 size={14} /></button>
                    </div>
                    <div className="mt-3 grid gap-3 lg:grid-cols-[0.8fr_1.2fr_0.7fr_1.3fr]">
                      <label className="flex min-h-10 items-center gap-2 text-xs font-semibold text-eco-ink/65">
                        <input data-testid={`costing-show-client-${request.id}-${index}`} type="checkbox" checked={line.show_to_client} onChange={(e) => setLine(index, 'show_to_client', e.target.checked)} className="h-4 w-4 accent-eco-green" />
                        Mostrar valor ao cliente
                      </label>
                      {line.show_to_client && <input data-testid={`costing-client-label-${request.id}-${index}`} value={line.client_label} onChange={(e) => setLine(index, 'client_label', e.target.value)} placeholder="Texto visível ao cliente" className={inputCls} />}
                      {line.show_to_client && <input data-testid={`costing-client-value-${request.id}-${index}`} type="number" min="0" step="0.01" value={line.client_price_eur} onChange={(e) => setLine(index, 'client_price_eur', e.target.value)} placeholder="Valor visível €" className={inputCls} />}
                      <input data-testid={`costing-internal-note-${request.id}-${index}`} value={line.internal_note} onChange={(e) => setLine(index, 'internal_note', e.target.value)} placeholder="Nota interna (privada)" className={inputCls} />
                    </div>
                  </div>
                ))}
              </div>

              <button type="button" data-testid={`costing-add-line-${request.id}`} onClick={() => { setLines((current) => [...current, emptyLine]); setDirty(true); }} className="eco-btn-secondary mt-3 min-h-10 px-4 text-xs"><Plus size={14} /> Adicionar linha</button>

              <div className="eco-number mt-4 grid gap-2 rounded-xl bg-eco-deep p-4 text-sm text-white sm:grid-cols-3" data-testid={`costing-totals-${request.id}`}>
                <span>Custo NET <strong className="block text-lg">{formatEur(totals.net)}</strong></span>
                <span>Margem Eco <strong className="block text-lg text-eco-gold">{formatEur(totals.marginAmount)}</strong></span>
                <span>Total cliente <strong className="block text-lg text-eco-gold">{formatEur(totals.clientTotal)}</strong></span>
              </div>
              {totals.selectedClientTotal > 0 && (
                <p className={`mt-2 text-xs ${Math.abs(totals.selectedClientTotal - totals.clientTotal) > 0.009 ? 'text-amber-700' : 'text-eco-green'}`} data-testid={`costing-selected-total-${request.id}`}>
                  Valores explicitamente selecionados para o cliente: {formatEur(totals.selectedClientTotal)}{Math.abs(totals.selectedClientTotal - totals.clientTotal) > 0.009 ? ' — não precisam de somar o total calculado; reveja antes de enviar.' : ''}
                </p>
              )}

              <textarea data-testid={`costing-internal-notes-${request.id}`} rows={2} value={internalNotes} onChange={(e) => { setInternalNotes(e.target.value); setDirty(true); }} placeholder="Notas internas do costing (privadas)" className={`${inputCls} mt-3`} />

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button type="button" data-testid={`costing-apply-${request.id}`} onClick={applyToProposal} disabled={saving} className="eco-btn-primary min-h-11 px-5 text-sm disabled:opacity-60">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Apply to proposal
                </button>
                {dirty && <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800" data-testid={`costing-unsaved-${request.id}`}>Alterações temporárias — ainda não guardadas</span>}
                {savedDraft && <span className="text-xs text-eco-ink/50" data-testid={`costing-saved-${request.id}`}>Rascunho privado guardado em {new Date(savedDraft.updated_at).toLocaleString('pt-PT')}</span>}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
};
